/* eslint-disable jsx-a11y/anchor-is-valid */
import { IonSpinner, IonToast, useIonToast } from '@ionic/react';
import { FullEvent, FullPost, Post, objectEquals } from 'model';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useDeletePostMutation, useGetPostQuery, useUpdatePostMutation } from '../api/PostApi';
import { EventEditFields } from '../components/EventEditFields';
import FormFooter from '../components/FormFooter';
import { Header } from '../components/Header';
import { IconButton } from '../components/IconButton';
import { IconCheck, IconClose } from '../components/QuirkIcons';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { QuirkIonPage } from '../components/QuirkIonPage';
import { EventEditRules } from '../formConfigs/EditEventFieldsConfig';
import { useFormValues } from '../hooks/useFormValues';

export const EditEvent: React.FC = () => {
	const { postid } = useParams<{ postid: Post['_id'] }>();
	const { data: fetchEvent } = useGetPostQuery(postid, { skip: !postid });
	const [event, setEvent] = useState<FullPost | undefined | null>(fetchEvent);
	const history = useHistory();
	const [deleteItem] = useDeletePostMutation();
	const [updatePost, { isError: updateIsError, isLoading: updateIsLoading }] = useUpdatePostMutation();
	const {
		setValues,
		formSuccess,
		formValues,
		validateForm,
		stateDataObject: newEvent
	} = useFormValues(EventEditRules, event as FullEvent, objectEquals);

	const [showToast] = useIonToast();
	const onSubmit = async () => {
		const compiledNoErrors = validateForm();
		if (compiledNoErrors && formValues && newEvent) {
			if (event) {
				const result = await updatePost({
					post: {
						...event,
						...newEvent,
						draft: false
					}
				});
				if (!('error' in result)) {
					setEvent(result.data);
					showToast({
						color: 'success',
						message: 'Event has been updated',
						position: 'top',
						duration: 1500
					});
					history.push('/main/latest');
				} else {
					showToast({
						color: 'danger',
						message: 'Error updating event',
						position: 'top',
						duration: 1500
					});
				}
			}
		}
	};

	const onCancel = async () => {
		history.go(-1);

		if (fetchEvent && fetchEvent.draft) {
			await deleteItem(fetchEvent._id);
		}
	};

	useEffect(() => {
		if (fetchEvent) {
			setEvent(fetchEvent);
		}
		return () => setEvent(undefined);
	}, [setEvent, fetchEvent]);

	return (
		<QuirkIonPage name="Edit Event" data={{ event: postid }}>
			<Header
				title="Edit Event"
				left={<IconButton icon={<IconClose />} onClick={onCancel} />}
				right={
					<IconButton
						fill="solid"
						label="Done"
						disabled={!formSuccess}
						icon={<IconCheck />}
						onClick={onSubmit}
						testId="editPostDoneButton"
					/>
				}
			/>
			<QuirkIonContent flush padding>
				<div>
					{event && formValues && (
						<EventEditFields
							setValues={setValues}
							setEvent={setEvent}
							formValues={formValues}
							event={event}
						/>
					)}
					{event && formValues && (
						<FormFooter onCancel={onCancel} onSubmit={onSubmit} disableSubmit={!formSuccess} />
					)}
					<IonSpinner name="crescent" hidden={!updateIsLoading} />
					<IonToast
						isOpen={updateIsError}
						duration={1500}
						message={(updateIsError as any)?.data}
						position="top"
						color="danger"
					/>
				</div>
			</QuirkIonContent>
		</QuirkIonPage>
	);
};
